import { Injectable } from '@angular/core';

import {
  OUTSTANDING_SINGLE_PAYMENT_MARKUP,
  OUTSTANDING_MULTIPLE_PAYMENT_MARKUP,
  OUTSTANDING_SINGLE_PAYMENT_FOR_UPDATE_MARKUP,
  OUTSTANDING_MULTIPLE_PAYMENT_FOR_UPDATE_MARKUP,
  UPDATE_PAYMENT_LINK_MARKUP,
  CREDIT_CARD_REVIEW_MARKUP,
  DIRECT_DEBIT_REVIEW_MARKUP,
  CREDIT_CARD_SUCCESS_MARKUP,
  DIRECT_DEBIT_SUCCESS_MARKUP,
  SOSURE_PREAMBLE_BACS_MARKUP,
  SOSURE_PREAMBLE_NONE_MARKUP
} from '../constants/payment-markup';
import {
  PAYMENT_PRODUCT_ICONS,
  PAYMENT_HERO_ICONS,
  PAYMENT_METHODS
} from '../constants/payment.constants';

// libraries
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {

  constructor() { }

  getPaymentReviewMarkup(paymentMethod: any, policySlug: string = '', showUpdatePaymentLink: boolean = false): any {
    let paymentSummaryMarkUp = ''
    let updatePaymentLinkMarkup = '';

    if (showUpdatePaymentLink) {
      updatePaymentLinkMarkup = UPDATE_PAYMENT_LINK_MARKUP;
      updatePaymentLinkMarkup = updatePaymentLinkMarkup.replace('{{policySlug}}', policySlug);
    }

    if (paymentMethod?.type === 'credit_card') {
      paymentSummaryMarkUp = this.createCreditCardSummary(CREDIT_CARD_REVIEW_MARKUP, paymentMethod);
    } else if (paymentMethod?.type === 'direct_debit') {
      paymentSummaryMarkUp = this.createDirectDebitSummary(DIRECT_DEBIT_REVIEW_MARKUP, paymentMethod);
    } else if (paymentMethod?.type === 'bacs_debit') {
      paymentSummaryMarkUp = this.createDirectDebitSummary(DIRECT_DEBIT_REVIEW_MARKUP, paymentMethod);
    }

    return paymentSummaryMarkUp + updatePaymentLinkMarkup;
  }

  getPaymentSuccessMarkup(paymentMethod: any): any {
    if (!paymentMethod) {
      return '';
    } else if (paymentMethod.type === 'credit_card') {
      return this.createCreditCardSummary(CREDIT_CARD_SUCCESS_MARKUP, paymentMethod);
    } else if (paymentMethod.type === 'direct_debit') {
      return this.createDirectDebitSummary(DIRECT_DEBIT_SUCCESS_MARKUP, paymentMethod);
    } else if (paymentMethod.type === 'bacs_debit') {
      return this.createDirectDebitSummary(DIRECT_DEBIT_SUCCESS_MARKUP, paymentMethod);
    }
  }

  createCreditCardSummary(template: string, paymentMethod: any): string {
    let markup = template;

    if (paymentMethod?.card_type) {
      markup = markup.replace('{{icon}}', PAYMENT_METHODS.icon[paymentMethod?.card_type]);
      markup = markup.replace('{{cardType}}', PAYMENT_METHODS.label[paymentMethod?.card_type]);
    }
    if (paymentMethod?.masked_number) {
      markup = markup.replace('{{cardNumber}}', (this.createMaskedDigits(4) + ' ' + paymentMethod?.masked_number.slice(-4)));
    }
    return markup;
  }

  createDirectDebitSummary(template: string, paymentMethod: any): string {
    let markup = template;
    let lastFourDigitsAccountNumber = '';
    let maskedAccountNumber: any = '';
    let accountType = 'BSB';
    let bsb = '';
    let bsbNum: any = '';
    let accountNumber = '';

    if (paymentMethod?.account_number) {
      lastFourDigitsAccountNumber = paymentMethod?.account_number.slice(-4);
      maskedAccountNumber = Number(paymentMethod?.account_number.length - lastFourDigitsAccountNumber.length);
    } else if (paymentMethod?.masked_number) {
      lastFourDigitsAccountNumber = paymentMethod?.masked_number.slice(-4);
      maskedAccountNumber = Number(paymentMethod?.masked_number.length - lastFourDigitsAccountNumber.length);
    }
    if (lastFourDigitsAccountNumber && maskedAccountNumber) {
      accountNumber = this.createMaskedDigits(maskedAccountNumber) + lastFourDigitsAccountNumber;
    }

    if (paymentMethod?.bsb) {
      bsbNum = paymentMethod.bsb.slice(-4);
    } else if (paymentMethod?.debit_sort_code) {
      bsbNum = paymentMethod.debit_sort_code.slice(-4);
      accountType = 'Sort Code';
    }
    if (bsbNum) {
      bsb = this.createMaskedDigits(3) + bsbNum.slice(-4);
    }

    markup = markup.replace('{{accountName}}', paymentMethod?.account_name || paymentMethod?.cardholder_name || '');
    markup = markup.replace('{{bankName}}', paymentMethod?.bank_name || '');
    markup = markup.replace('{{accountType}}', accountType);
    markup = markup.replace('{{bsb}}', bsb);
    markup = markup.replace('{{accountNumber}}', accountNumber);
    return markup;
  }

  createMaskedDigits(num: number) {
    const markup = '<span class="mat-icon material-icons masked-digit" aria-hidden="true">circle</span>';

    return markup.repeat(num);
  };

  addAnalytics(step: any, policy: any, paymentMethod: any) {
    step.analytics.policy_slug = policy.slug;
    step.analytics.user_slug = policy.user_slug;
    step.analytics.payment_type = paymentMethod?.type;
  }

  getOutstandingPaymentMarkup(overdueInvoices: any, currencySymbol: string, policySlug: string = '', isRaptorial: boolean, paymentFlow: string) {
    let outstandingPaymentMarkup = '';
    let outstandingPaymentsCount = overdueInvoices?.length;

    if (paymentFlow === 'update') {
      outstandingPaymentMarkup = outstandingPaymentsCount === 1 ?
        OUTSTANDING_SINGLE_PAYMENT_FOR_UPDATE_MARKUP :
        OUTSTANDING_MULTIPLE_PAYMENT_FOR_UPDATE_MARKUP;
    } else if (paymentFlow === 'retry') {
      outstandingPaymentMarkup = outstandingPaymentsCount === 1 ?
        OUTSTANDING_SINGLE_PAYMENT_MARKUP :
        OUTSTANDING_MULTIPLE_PAYMENT_MARKUP;
    }
    let totalDues = this.getDueDetails(overdueInvoices, isRaptorial);

    outstandingPaymentMarkup = outstandingPaymentMarkup.replace(
      '{{currencySymbol}}',
      currencySymbol
    );
    outstandingPaymentMarkup = outstandingPaymentMarkup.replace(
      '{{policySlug}}',
      policySlug
    );

    // Multiple invoices overdue. We show a combined sum amount
    if (outstandingPaymentsCount > 1) {
      outstandingPaymentMarkup = outstandingPaymentMarkup.replace('{{paymentAmount}}', totalDues.amount);
    } else if (outstandingPaymentsCount === 1) {
      // Single invoice overdue. We show an amount and due date
      outstandingPaymentMarkup = outstandingPaymentMarkup.replace('{{paymentAmount}}', totalDues.amount);
      outstandingPaymentMarkup = outstandingPaymentMarkup.replace('{{paymentDueDate}}', totalDues.dueDate);
    }
    return outstandingPaymentMarkup;
  }

  getDueDetails(invoices: any, isRaptorial: boolean) {
    let outstandingPaymentsCount = invoices ? invoices.length : 0;
    let totalDues:any = {
      amount: 0,
      dueDate: outstandingPaymentsCount === 1 ?
        moment(invoices[0].payment_due_date).format('DD MMM YYYY'):
        null
    }
    invoices.forEach((invoice: any) => {
      let taxes = isRaptorial ? this.getTotalTaxOnInvoice(invoice.taxes) :
      (
        parseFloat(invoice.tax_fsl) +
        parseFloat(invoice.tax_duty) +
        parseFloat(invoice.tax_gst)
      )
      let invoiceDue = {
        amount: parseFloat(invoice.base_premium) + taxes,
        dueDate: moment(invoice.payment_due_date).format('DD MMM YYYY')
      }
      totalDues.amount = Number((totalDues.amount + invoiceDue.amount).toFixed(2));
    });
    return totalDues;
  }

  getTotalTaxOnInvoice(taxes: any) {
    let totalTax = 0;

    for(const key in taxes) {
      totalTax = totalTax + parseFloat(taxes[key]);
    }

    return totalTax;
  }
}
